import { useEffect, useRef, useState } from 'react'
import { ClassEndWarningContent } from 'utils/constants'

export const CLASS_END_WARNING_MINUTES = 5

export const formatClassEndCountdown = (totalSeconds: number): string => {
    const clamped = Math.max(0, Math.floor(totalSeconds))
    const minutes = Math.floor(clamped / 60).toString().padStart(2, '0')
    const seconds = (clamped % 60).toString().padStart(2, '0')

    return `${minutes}:${seconds}`
}

export const buildClassEndWarningMessage = (totalSeconds: number): string => (
    ClassEndWarningContent.message(formatClassEndCountdown(totalSeconds))
)

type ClassEndCountdown = {
    isVisible: boolean
    message: string
    dismiss: () => void
}

const getStartSeconds = (timeLeftMinutes: number, warningMinutes: number): number => (
    Math.min(timeLeftMinutes, warningMinutes) * 60
)

export const useClassEndCountdown = (
    enabled: boolean,
    timeLeftMinutes: number,
    warningMinutes: number = CLASS_END_WARNING_MINUTES
): ClassEndCountdown => {
    const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null)
    const [dismissed, setDismissed] = useState(false)
    const hasStarted = useRef(false)

    const dismiss = (): void => {
        setDismissed(true)
    }

    useEffect(() => {
        if (!enabled) {
            hasStarted.current = false
            setRemainingSeconds(null)
            setDismissed(false)
            return
        }

        const inWarningWindow = timeLeftMinutes > 0 && timeLeftMinutes <= warningMinutes

        if (!inWarningWindow || hasStarted.current) {
            return
        }

        hasStarted.current = true
        setRemainingSeconds(getStartSeconds(timeLeftMinutes, warningMinutes))
    }, [enabled, timeLeftMinutes, warningMinutes])

    const isTicking = remainingSeconds !== null && remainingSeconds > 0

    useEffect(() => {
        if (!isTicking) {
            return
        }

        const intervalId = setInterval(() => {
            setRemainingSeconds(previous => {
                if (previous === null || previous <= 1) {
                    return 0
                }

                return previous - 1
            })
        }, 1000)

        return () => {
            clearInterval(intervalId)
        }
    }, [isTicking])

    if (remainingSeconds === null) {
        return {
            isVisible: false,
            message: '',
            dismiss
        }
    }

    return {
        isVisible: !dismissed,
        message: buildClassEndWarningMessage(remainingSeconds),
        dismiss
    }
}
